'use client';
import { FC, Suspense, useState, useEffect } from 'react';
import {
  CatButton,
  CatHeading,
  CatIconArrowForward
} from 'components/blocks-components';
import { useTranslations } from 'next-intl';
import {
  Conditional,
  Drawer,
  DrawerBody,
  DrawerHeader,
  useDrawer
} from '@cat-ecom/pcc-components';
import {
  PartDetailsAndSchematicResponse,
  productPartsData
} from 'types/product-diagram';
import cx from 'classnames';
import styles from './view-diagram-drawer.module.scss';
import { DealerData } from 'types/parts-diagram';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import PartsItem from 'components/parts-item';
import ProductDiagramDrawer from '../product-diagram-drawer/product-diagram-drawer';
import ProductQuickViewDrawer from 'components/product-quick-view-drawer';
import { PARTS_DIAGRAM_PAGE } from 'constants/analyticsConstants';
import { itemSummaryApiCall } from 'services/parts-diagram/actions';
import { usePriceAndAvailabilityQuery } from 'services/priceAvailability/use-query-price-availability';
import { getPrice } from 'utils/product-diagram';
import { getPricePartsList, parsePriceToFloat } from '../../utils';
import { DealerResponse } from 'services/dealer';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';
import { GA_PDP_PARTS_DIAGRAM } from 'constants/analyticsConstants';
import { CartResponse } from 'services/cart/types';
import { Equipment } from 'services/equipment/types';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { parseItemIdAndName } from 'utils/common';

export interface PartsDiagramButtonDrawerProps {
  model?: string;
  partNumber?: string;
  partDetailsAndSchematicResponse?: PartDetailsAndSchematicResponse[];
  contactDealerBtn?: React.ReactNode;
  isRTL?: boolean;
  isPccPdpHeaderUpdateMobile?: boolean;
  dealerData: DealerData;
  storeId: string;
  langId: string;
  isCatCorp: boolean;
  selectedEquipment: Equipment;
  shouldShowPartsDiagramV2: boolean;
  stickyContactDealerBtn: React.ReactNode;
  cartServerBtn?: React.ReactNode;
  isSystemAndComponentPageFlag?: boolean;

  dealerInformation?: DealerResponse;
  storeFeatures: { [key: string]: boolean };
  isShowSourcingDetailEnabled: boolean;
  isDeliveryEnabled: boolean;
  locale: string;
  cartDetails?: CartResponse;
  productData?: productPartsData;
}

const ViewAllDiagramsDrawer: FC<PartsDiagramButtonDrawerProps> = ({
  model,
  partNumber,
  partDetailsAndSchematicResponse,
  contactDealerBtn,
  isRTL,
  isPccPdpHeaderUpdateMobile,
  dealerData,
  storeId,
  langId,
  isCatCorp,
  selectedEquipment,
  shouldShowPartsDiagramV2,
  stickyContactDealerBtn,
  cartServerBtn,
  isSystemAndComponentPageFlag,
  dealerInformation,
  storeFeatures,
  isShowSourcingDetailEnabled,
  isDeliveryEnabled,
  locale,
  cartDetails,
  productData
}) => {
  const [drawerActive, setDrawerActive] = useState(false);
  const [itemSummaryCardData, setItemSummaryCardData] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [item, setItem] = useState(null);
  const [isActiveListTab, setIsActiveListTab] = useState(false);
  const [pricePartsList, setPricePartsList] = useState(null);
  const t = useTranslations();
  const { drawerProps, openDrawer, closeDrawer, isDrawerOpen } = useDrawer();
  const {
    drawerProps: productDiagramDrawerProps,
    closeDrawer: productDiagramCloseDrawer,
    openDrawer: productDiagramOpenDrawer
  } = useDrawer();
  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();

  const drawertitle = replaceTokensInString(
    t('BOTTOMUP_DRAWER_TEXT', {
      0: model,
      1: partNumber
    })
  );

  const { setIsCatCorp, setServerData } = usePartDiagram();

  useEffect(() => {
    setIsCatCorp(isCatCorp);
  }, [isCatCorp, setIsCatCorp]);

  useEffect(() => {
    setServerData((prevServerData) => ({
      ...prevServerData,
      locale,
      dealerData,
      storefeatures: storeFeatures,
      dealerInformation,
      isShowSourcingDetailEnabled,
      isDeliveryEnabled
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locale,
    dealerData,
    storeFeatures,
    dealerInformation,
    isShowSourcingDetailEnabled,
    isDeliveryEnabled
  ]);

  const [selectedDiagramData, setSelectedDiagramData] = useState(
    partDetailsAndSchematicResponse[0]
  );

  const {
    data: priceDataResponse,
    isSuccess: isPriceSuccess,
    isLoading
  } = usePriceAndAvailabilityQuery(
    pricePartsList
      ? {
          storeId,
          langId,
          partsList: pricePartsList
        }
      : {
          storeId,
          langId,
          partsList: []
        }
  );

  const handleOpen = async (
    partNumber: string,
    price: ProductDealerPriceAvailability
  ) => {
    !isCatCorp && setPriceData(price);
    const itemSummaryResponse = await itemSummaryApiCall(partNumber);
    setItemSummaryCardData(itemSummaryResponse);
  };

  const handleOpenDrawer = () => {
    if (partDetailsAndSchematicResponse.length > 1) {
      openDrawer();
    } else if (partDetailsAndSchematicResponse.length === 1) {
      setPricePartsList(getPricePartsList(partDetailsAndSchematicResponse[0]));
      setSelectedDiagramData(partDetailsAndSchematicResponse[0]);
      setIsActiveListTab(partDetailsAndSchematicResponse[0]?.graphicSVG === '');
      productDiagramOpenDrawer();
    }
  };
  const handleProductDiagramOpenDrawer = (partItem) => {
    const partsList = getPricePartsList(partItem);
    setPricePartsList(partsList);
    setIsActiveListTab(partItem?.graphicSVG === '');
    setSelectedDiagramData(partItem);
    productDiagramOpenDrawer();
  };

  useEffect(() => {
    if (
      pricePartsList &&
      isPriceSuccess &&
      priceDataResponse &&
      selectedDiagramData
    ) {
      const combinedData = getPrice(
        [selectedDiagramData],
        priceDataResponse
      )[0];
      setItem({ ...combinedData });
    } else if (selectedDiagramData) {
      setItem(selectedDiagramData);
    }

    if (!isLoading && isPriceSuccess) {
      const itemSummaryWithIndex = Array.isArray(item?.lineItems)
        ? item.lineItems.map((lineItem, idx) => {
            const totalPrice = parsePriceToFloat(
              lineItem?.priceAndAvailabilityData?.totalPrice ||
                productData?.price ||
                '0'
            );
            return {
              ...lineItem,
              index: parseInt(lineItem?.referenceNumber) || idx,
              imageURL: '',
              type: 'part',
              categoryPath:
                lineItem?.priceAndAvailabilityData?.categoryPath ||
                productData?.categoryPath,
              manufacturer: productData?.manufacturer,
              partNumber: lineItem?.partNumber || partNumber,
              unformattedTotalPrice: totalPrice,
              quantity: Number(lineItem?.quantity) || 1
            };
          })
        : item?.lineItems;
      fireSelectItemEvent({
        itemListId: GA_PDP_PARTS_DIAGRAM,
        itemListName: GA_PDP_PARTS_DIAGRAM,
        partName: item.name || '',
        locationId: '',
        isSearchPageEvent: true,
        itemSummary: itemSummaryWithIndex,
        serialNumber: '',
        fitment: '',
        item: {
          orderItemId: cartDetails?.orderHeader?.orderId,
          partNumber: '',
          quantity: 0,
          availabilityMessage: ''
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    priceDataResponse,
    isPriceSuccess,
    isLoading,
    selectedDiagramData,
    fireSelectItemEvent,
    cartDetails?.orderHeader?.orderId
  ]);

  useEffect(() => {
    if (isDrawerOpen && drawerProps.isActive) {
      const gaItems = partDetailsAndSchematicResponse.map((part) => {
        const { itemId, itemName } = parseItemIdAndName(part?.title || '');

        return {
          ...part,
          imageURL: '',
          type: 'part',
          manufacturer: productData?.manufacturer,
          partNumber: itemId,
          name: itemName,
          categoryPath: productData?.categoryPath,
          price: productData?.price
        };
      });

      const diagramViewItemList = {
        item_list_id: GA_PDP_PARTS_DIAGRAM,
        item_list_name: GA_PDP_PARTS_DIAGRAM,
        currency: cartDetails?.orderItemPriceAvailabilityTotals?.currency
      };

      const diagramCartPayload = {
        products: gaItems,
        itemListData: diagramViewItemList,
        isProductsData: true,
        isSearchPageEvent: true,
        cartItemsData: cartDetails,
        cartId: cartDetails?.orderHeader?.orderId
      };
      if (!drawerActive) {
        fireViewItemListEvent(diagramCartPayload);
      }
    }
  }, [
    isDrawerOpen,
    partDetailsAndSchematicResponse,
    fireViewItemListEvent,
    cartDetails,
    partNumber,
    productData,
    drawerActive,
    drawerProps.isActive
  ]);

  return (
    <>
      <CatButton
        data-testid="cat-button"
        className="align-self-end"
        onClick={handleOpenDrawer}
      >
        {t('PDP_PARTS_DIAGRAM_VIEWDIAGRAMS')}
        <CatIconArrowForward slot="after" />
      </CatButton>

      <>
        <Conditional
          test={partDetailsAndSchematicResponse?.length > 1 && isDrawerOpen}
        >
          <Drawer
            className={cx(
              styles['footer-button-mobile-drawer__header'],
              styles['footer-button-mobile-drawer__body']
            )}
            isActive={isDrawerOpen}
            data-testId="view-diagram-mobile-drawer"
            orientation="bottom"
            verticalMaxHeight="large"
            onBlDrawerClose={closeDrawer}
            hasBackdrop={false}
            {...drawerProps}
          >
            <DrawerHeader>
              <CatHeading variant="title-sm">
                {t('MA_SP_Parts_Diagrams')}
              </CatHeading>
              <CatHeading variant="body-sm">{drawertitle}</CatHeading>
            </DrawerHeader>
            <DrawerBody className="d-flex flex-column gap-3">
              {partDetailsAndSchematicResponse.map((part) => (
                <PartsItem
                  key={part.graphicControlNumber}
                  graphicSVG={part.graphicSVG}
                  title={part.title}
                  onClick={() => handleProductDiagramOpenDrawer(part)}
                />
              ))}
            </DrawerBody>
          </Drawer>
        </Conditional>
        <ProductDiagramDrawer
          drawerProps={productDiagramDrawerProps}
          closeDrawer={productDiagramCloseDrawer}
          partItem={item}
          contactDealerBtn={contactDealerBtn}
          stickyContactDealerBtn={stickyContactDealerBtn}
          isRTL={isRTL}
          isPccPdpHeaderUpdateMobile={isPccPdpHeaderUpdateMobile}
          handleOpen={handleOpen}
          setDrawerActive={setDrawerActive}
          isCatCorp={isCatCorp}
          isActiveListTab={isActiveListTab}
          setIsActiveListTab={setIsActiveListTab}
          selectedEquipment={selectedEquipment}
          shouldShowPartsDiagramV2={shouldShowPartsDiagramV2}
          isLoading={isLoading}
          cartServerBtn={cartServerBtn}
          isSystemAndComponentPageFlag={isSystemAndComponentPageFlag}
          productData={productData}
          partNumber={partNumber}
          cartDetails={cartDetails}
          fromProductPage={true}
        />

        {itemSummaryCardData && drawerActive && (
          <Suspense fallback={null}>
            <ProductQuickViewDrawer
              data={itemSummaryCardData}
              priceData={priceData}
              dealerData={dealerData}
              currency={item.currency || ''}
              model={model}
              listEvent={PARTS_DIAGRAM_PAGE}
              drawerActive={drawerActive}
              setDrawerActive={setDrawerActive}
              isPccPdpHeaderUpdateMobile={isPccPdpHeaderUpdateMobile}
              isRTL={isRTL}
              currencyLdFeatureFlag={true}
              isPartDiagram={true}
            />
          </Suspense>
        )}
      </>
    </>
  );
};
export default ViewAllDiagramsDrawer;
